// End-game interstitial — the post-game-over sequence between the
// game-over event and the game-over modal. Mirrors the coin-flip
// ceremony in pacing.
//
// Two scenes, sequential:
//
//   Scene 1 — Responsible card slides in from the WINNER'S side,
//             speaks its `kill_opponent` line (if any). For deck-out
//             we synthesize a virtual "Out of cards" card (no speech).
//
//   Scene 2 — Opponent profile slides in from the OPPONENT'S side,
//             speaks `opponent_lose` (player won) or `opponent_win`
//             (opponent won). Missing line falls back to a generic
//             plate: "{cardname} is defeated." when the player won,
//             "{cardname} is victorious." when the opponent won.
//
// Rule: we NEVER speak for the player. The player's profile dossier
// is silent at game-end; both speech moments come from non-player
// cards (the responsible card, which may belong to either side; and
// the opponent profile, which is always on the opponent's side).
//
// Same-card collapse: if the responsible card IS the opponent
// profile (e.g. the opponent's King Arthur delivered the killing
// blow), the two scenes collapse into one element on screen, with
// one bubble drawn from the merged pool of kill_opponent +
// opponent_win/lose lines. Sequential rendering would repeat
// sentiment; a single random line lands harder.
//
// Skip on tap fast-forwards through all remaining timeouts and
// invokes `onComplete()` immediately.

import { getConfig } from '../config.js';
import type { CardDef, PlayerSide } from '../types.js';
import type { DossierPublic, VoiceTrigger } from '../shared/dossier-types.js';
import { lookupDossierOrNull } from '../collection/DossierCache.js';
import { clearAllVoiceBubbles } from './VoiceBubble.js';
import { pickVoiceLine, logVoiceLine } from './VoiceLinePool.js';

export interface InterstitialOptions {
  winner: PlayerSide;
  /** Card def of the card that delivered the killing blow. Null for
   *  deck-out (a virtual "Out of cards" plate renders instead). */
  responsibleCard: CardDef | null;
  /** Card def of the opponent's profile identity. Null if unknown
   *  (skirmish without a resolved opponent identity — shouldn't
   *  happen in v2 since we always resolve at coin-flip time, but
   *  handled gracefully). */
  opponentProfileCard: CardDef | null;
  /** Called once the interstitial finishes (either by playing out
   *  fully or by being skipped via tap). The game-over modal is
   *  responsible for being shown by the caller in this callback. */
  onComplete: () => void;
  /** Match game-log array to push voice-line entries into. Each line
   *  the interstitial decides to display is logged at decision time
   *  (not bubble-fire time) so the log entry reliably lands even if
   *  the player skips the interstitial before the bubble appears. */
  log: string[];
}

/** Run the interstitial. Inserts a fullscreen overlay into the DOM,
 *  runs the slide-in / speech-bubble scenes, then removes itself and
 *  fires `onComplete`. Returns nothing — the caller chains via
 *  `onComplete`. */
export function showEndGameInterstitial(opts: InterstitialOptions): void {
  // Snuff any in-flight bubbles so they don't bleed into the
  // interstitial scenes.
  clearAllVoiceBubbles();

  const overlay = document.createElement('div');
  overlay.className = 'endgame-interstitial-overlay';
  // Body class lets CSS pause/hide board decorations while the
  // interstitial is up. Specifically the animated battle-line's
  // compositing layer can get promoted above the fixed overlay on
  // some mobile browsers, leaving a bright red streak painted
  // across the responsible-card slide-in — see the rule under
  // `body.endgame-interstitial-active` in main.css.
  document.body.classList.add('endgame-interstitial-active');
  // Skip-on-tap. We capture clicks at the overlay level so taps on
  // the card or the bubble area both fast-forward.
  let completed = false;
  const finish = (): void => {
    if (completed) return;
    completed = true;
    for (const t of pendingTimers) clearTimeout(t);
    overlay.classList.add('endgame-interstitial-fading');
    setTimeout(() => {
      overlay.remove();
      document.body.classList.remove('endgame-interstitial-active');
      opts.onComplete();
    }, getConfig().voiceLineFadeMs);
  };
  overlay.addEventListener('click', finish);
  document.body.appendChild(overlay);

  const pendingTimers: Array<ReturnType<typeof setTimeout>> = [];
  const wait = (ms: number, fn: () => void): void => {
    const t = setTimeout(() => {
      pendingTimers.splice(pendingTimers.indexOf(t), 1);
      if (!completed) fn();
    }, ms);
    pendingTimers.push(t);
  };

  // Decide collapse: responsible card === opponent profile card.
  // If the responsible card is null (deck-out), there's nothing to
  // collapse with; deck-out always plays two scenes (the placeholder
  // plate, then the opponent profile).
  const collapseSameCard =
    !!opts.responsibleCard
    && !!opts.opponentProfileCard
    && opts.responsibleCard.id === opts.opponentProfileCard.id;

  // Resolve the line for the opponent's win/lose trigger and the
  // responsible card's kill_opponent line up-front so we know whether
  // each scene will speak.
  const opponentTrigger: VoiceTrigger = opts.winner === 'player' ? 'opponent_lose' : 'opponent_win';

  const responsibleDossier: DossierPublic | null = opts.responsibleCard
    ? lookupDossierOrNull(opts.responsibleCard.id)
    : null;
  const opponentDossier: DossierPublic | null = opts.opponentProfileCard
    ? lookupDossierOrNull(opts.opponentProfileCard.id)
    : null;

  const responsibleLine = pickLine(responsibleDossier, 'kill_opponent');
  const opponentLine = pickLine(opponentDossier, opponentTrigger);

  // Position rules. Player's responsible card slides from LEFT;
  // opponent's responsible card slides from RIGHT. Opponent profile
  // always slides from the RIGHT. Deck-out synthesizes a virtual card
  // on the winner's side.
  //
  // When the player wins, the two cards land on opposite sides
  // (responsible left, opponent profile right) and don't overlap.
  // When the OPPONENT wins, both land on the right — so we apply
  // staggered "stacked" positions: responsible higher and further
  // inward, opponent profile lower and closer to the right edge.
  // Looks like:
  //     |  Responsible  <-------- |
  //     |          Opponent <---- |
  const responsibleSide: 'left' | 'right' = opts.winner === 'player' ? 'left' : 'right';
  const stacked = opts.winner === 'opponent';

  // Scene 1: responsible card.
  const responsibleEl = renderResponsibleCard(
    opts.responsibleCard,
    responsibleSide,
    stacked ? 'stacked-primary' : null,
  );
  overlay.appendChild(responsibleEl);
  // Trigger slide-in via class swap on next tick. Also auto-shrink
  // long names in the same rAF so the font-size step happens on the
  // same frame as the layout for the appended card.
  requestAnimationFrame(() => {
    fitCardNameToBox(responsibleEl);
    responsibleEl.classList.add('endgame-slide-in');
  });

  const slideMs = getConfig().interstitialSlideMs;
  const dwellMs = getConfig().voiceLineDwellMs;
  const fadeMs = getConfig().voiceLineFadeMs;

  // Time accounting (ms from interstitial start, before slide-in finishes):
  //   t0 = 0                              — scene 1 slide starts
  //   t1 = slideMs                        — scene 1 slide done, bubble fades in
  //   t2 = t1 + fadeMs + dwellMs          — scene 1 bubble dwell done
  //   t3 = t2 + fadeMs                    — scene 1 bubble faded out
  //   t4 = t3                             — scene 2 slide starts (or finish, if collapsed)
  //   t5 = t4 + slideMs                   — scene 2 slide done, bubble fades in
  //   t6 = t5 + fadeMs + dwellMs          — scene 2 bubble dwell done
  //   t7 = t6 + fadeMs                    — scene 2 bubble faded out → finish
  //
  // If responsibleLine is missing, we still slide the card in but skip
  // its bubble (compress timing). Same for the opponent slot.

  // Scene 1 bubble.
  let cursor = slideMs;

  // Collapse case: responsible card === opponent profile. We show ONE
  // bubble on the single element. Pick the displayed line up front
  // so we log exactly what the player sees (not the discarded
  // pre-collapse responsibleLine).
  if (collapseSameCard) {
    let pickText: string | null = null;
    let pickVariant: 'speaker' | 'narrator' = 'narrator';
    let pickSpeakerName: string | null = null;

    if (responsibleLine && opponentDossier) {
      // Both pools live on the same dossier. Merge kill_opponent +
      // opponent_win/lose into one pool and draw a single random
      // line — repeating one of the original two arrays in
      // sequence would feel redundant given the sentiment overlap.
      const merged = mergedCollapseLines(opponentDossier, opponentTrigger);
      const pick = pickVoiceLine(merged);
      if (pick) {
        pickText = pick;
        pickVariant = opponentDossier.can_speak ? 'speaker' : 'narrator';
        pickSpeakerName = opts.opponentProfileCard?.name ?? null;
      }
    } else if (!responsibleLine && opponentLine) {
      // No kill_opponent line; use the win/lose line on the same
      // element after slide-in completes.
      pickText = opponentLine.text;
      pickVariant = opponentLine.variant;
      pickSpeakerName = opts.opponentProfileCard?.name ?? null;
    } else if (responsibleLine && !opponentDossier) {
      // Edge case — collapseSameCard but no opponent dossier means
      // we lost the opponent_win/lose pool. Fall back to the
      // responsibleLine pick we made earlier.
      pickText = responsibleLine.text;
      pickVariant = responsibleLine.variant;
      pickSpeakerName = opts.responsibleCard?.name ?? null;
    }

    if (pickText) {
      if (pickSpeakerName) {
        logVoiceLine(opts.log, pickSpeakerName, pickText, pickVariant === 'speaker');
      }
      const text = pickText;
      const variant = pickVariant;
      wait(slideMs, () => attachBubble(responsibleEl, text, variant));
      cursor = slideMs + fadeMs + dwellMs + fadeMs;
    }
    wait(cursor, finish);
    return;
  }

  // Non-collapse path: scene 1 plays the responsibleLine if any.
  if (responsibleLine && opts.responsibleCard) {
    logVoiceLine(opts.log, opts.responsibleCard.name, responsibleLine.text, responsibleLine.variant === 'speaker');
    wait(cursor, () => attachBubble(responsibleEl, responsibleLine.text, responsibleLine.variant));
    cursor += fadeMs + dwellMs + fadeMs;
  }

  // Scene 2: opponent profile slides in (separate element).
  const opponentEl = opts.opponentProfileCard
    ? renderProfileCard(opts.opponentProfileCard, 'right', stacked ? 'stacked-secondary' : null)
    : renderFallbackProfilePlate(stacked ? 'stacked-secondary' : null);
  // Wait for scene 1 to finish before adding scene 2's slide.
  wait(cursor, () => {
    overlay.appendChild(opponentEl);
    requestAnimationFrame(() => {
      fitCardNameToBox(opponentEl);
      opponentEl.classList.add('endgame-slide-in');
    });
  });
  cursor += slideMs;

  // Scene 2 bubble. Fallback text if no line — the generic plate
  // isn't a real voice line, so it's NOT logged. Real dossier lines
  // log normally. The fallback is win/lose-aware: the opponent is
  // "defeated" only when the player WON; when the opponent won, they
  // are "victorious" (otherwise a loss would wrongly read as the
  // opponent being defeated — e.g. "Lakshmi is defeated" after the
  // player loses to Lakshmi).
  const opponentName = opts.opponentProfileCard?.name ?? 'Opponent';
  const opponentBubbleText = opponentLine?.text
    ?? (opts.winner === 'player' ? `${opponentName} is defeated.` : `${opponentName} is victorious.`);
  const opponentBubbleVariant = opponentLine?.variant ?? 'narrator';
  if (opponentLine && opts.opponentProfileCard) {
    logVoiceLine(opts.log, opts.opponentProfileCard.name, opponentLine.text, opponentLine.variant === 'speaker');
  }
  wait(cursor, () => attachBubble(opponentEl, opponentBubbleText, opponentBubbleVariant));
  cursor += fadeMs + dwellMs + fadeMs;

  wait(cursor, finish);
}

// ── Rendering helpers ──────────────────────────────────────────────

type StackPosition = 'stacked-primary' | 'stacked-secondary' | null;

function renderResponsibleCard(
  card: CardDef | null,
  side: 'left' | 'right',
  stack: StackPosition,
): HTMLElement {
  if (!card) {
    return renderDeckOutPlate(side, stack);
  }
  return renderProfileCard(card, side, stack);
}

/** Shrink an endgame card's name font size until the text fits in
 *  its 20%-of-card name box without vertical overflow. Steps down 1px
 *  at a time from the CSS default (14px) to a minimum of 9px. Below
 *  that we bail out and let the container's `overflow: hidden` clip
 *  the tail — only reachable for absurdly long names (~50+ chars).
 *
 *  Callers invoke this after the card is attached to the DOM so
 *  `scrollHeight` / `clientHeight` return real values. Reading those
 *  triggers a layout, so we batch inside a single rAF at the call
 *  site to avoid a per-frame reflow storm. */
function fitCardNameToBox(cardEl: HTMLElement): void {
  const box = cardEl.querySelector('.endgame-card-name') as HTMLElement | null;
  const span = box?.querySelector('span') as HTMLElement | null;
  if (!box || !span) return;
  const MAX = 14;
  const MIN = 9;
  for (let size = MAX; size >= MIN; size--) {
    span.style.fontSize = `${size}px`;
    if (span.scrollHeight <= box.clientHeight) return;
  }
  // Sticks at MIN if it never fit — the box's overflow:hidden will
  // clip the residual, which is the acceptable fallback for a name
  // that's still too long at 9px.
}

function renderProfileCard(card: CardDef, side: 'left' | 'right', stack: StackPosition): HTMLElement {
  const el = document.createElement('div');
  el.className = `endgame-card endgame-${side}${stack ? ` endgame-card-${stack}` : ''}`;
  // No card-detail click on the interstitial cards themselves —
  // clicking anywhere on the overlay skips. Avatar-style crop reused
  // from the ladder so the image fits comfortably without showing
  // text/border chrome.
  el.innerHTML = `
    <img src="${escapeAttr(card.art)}" alt="${escapeAttr(card.name)}" draggable="false"
         onerror="this.style.display='none'" />
    <div class="endgame-card-name"><span>${escapeHtml(card.name)}</span></div>
  `;
  return el;
}

function renderDeckOutPlate(side: 'left' | 'right', stack: StackPosition): HTMLElement {
  const el = document.createElement('div');
  el.className = `endgame-card endgame-${side} endgame-deckout-plate${stack ? ` endgame-card-${stack}` : ''}`;
  el.innerHTML = `<div class="endgame-card-name">Out of cards</div>`;
  return el;
}

function renderFallbackProfilePlate(stack: StackPosition): HTMLElement {
  // No opponent identity known. Render an empty-ish plate so the
  // generic "{name} is defeated." line still has something to attach
  // to.
  const el = document.createElement('div');
  el.className = `endgame-card endgame-right endgame-fallback-plate${stack ? ` endgame-card-${stack}` : ''}`;
  el.innerHTML = `<div class="endgame-card-name">Opponent</div>`;
  return el;
}

function attachBubble(target: HTMLElement, text: string, variant: 'speaker' | 'narrator'): void {
  // The bubble is rendered as a child of the slide-in element so it
  // animates together rather than via VoiceBubble's fixed-position
  // path (which is rect-snapshot based and would race with the
  // ongoing slide animation).
  const bubble = document.createElement('div');
  bubble.className = `voice-bubble voice-bubble-${variant} endgame-bubble`;
  bubble.style.opacity = '0';
  bubble.style.transition = `opacity ${getConfig().voiceLineFadeMs}ms ease`;
  bubble.textContent = text;
  target.appendChild(bubble);
  // Force layout flush so the opacity transition fires AND so
  // offsetWidth reflects the bubble's wrapped width before we clamp.
  void bubble.offsetWidth;
  clampInterstitialBubble(target, bubble);
  bubble.style.opacity = '1';
  // Bubble fades back out as part of the scene timing — managed by
  // the outer cursor schedule. We don't auto-remove here; the overlay
  // fade-out on `finish()` takes everything with it.
}

/** Shift a slide-in card's bubble horizontally so it stays inside the
 *  viewport when the card is hugging an edge (mobile portrait, where
 *  the responsible card lands at `left: 6%` and the opponent profile
 *  lands at `right: 6%`). The bubble is centered on the card by
 *  default (`left: 50%` + `translateX(-50%)`), so a wide bubble on a
 *  corner card overflows the screen and gets clipped.
 *
 *  Implementation mirrors the in-game VoiceBubble clamp: derive the
 *  shift from card-center + bubble width + viewport width (all
 *  transform-independent inputs) so the result is stable. The card
 *  doesn't move during the interstitial dwell, so a one-shot clamp
 *  suffices — no rAF tracking needed. */
function clampInterstitialBubble(target: HTMLElement, bubble: HTMLElement): void {
  const margin = 8;
  const cardRect = target.getBoundingClientRect();
  const cardCenterX = cardRect.left + cardRect.width / 2;
  const bubbleWidth = bubble.offsetWidth;
  const unshiftedLeft = cardCenterX - bubbleWidth / 2;
  const maxLeft = window.innerWidth - margin - bubbleWidth;
  const clampedLeft = Math.max(margin, Math.min(maxLeft, unshiftedLeft));
  const shiftX = clampedLeft - unshiftedLeft;
  if (shiftX !== 0) {
    bubble.style.transform = `translateX(calc(-50% + ${shiftX}px))`;
  }
  // Keep the tail aimed at the card center even after the shift.
  // Speakers use a tail; narrators don't render one, but setting the
  // variable is harmless on those.
  bubble.style.setProperty('--tail-x', `calc(50% - ${shiftX}px)`);
}

interface LinePick { text: string; variant: 'speaker' | 'narrator'; }

function pickLine(dossier: DossierPublic | null, trigger: VoiceTrigger): LinePick | null {
  if (!dossier) return null;
  const lines = dossier.voice_lines[trigger];
  if (!lines || lines.length === 0) return null;
  const text = pickVoiceLine(lines);
  if (!text) return null;
  return { text, variant: dossier.can_speak ? 'speaker' : 'narrator' };
}

/** Merge `kill_opponent` + the chosen opponent trigger arrays into a
 *  single pool for same-card collapse. Duplicates are not removed —
 *  the chance of overlap across the two arrays is low and the LLM-
 *  written content varies enough that a coincidental match is fine. */
function mergedCollapseLines(dossier: DossierPublic, opponentTrigger: VoiceTrigger): string[] {
  return [
    ...(dossier.voice_lines.kill_opponent ?? []),
    ...(dossier.voice_lines[opponentTrigger] ?? []),
  ];
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function escapeAttr(s: string): string { return escapeHtml(s); }
